import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BatchPickService } from '../batchpick.service';
import { BatchPickState } from '../models/batchpick-state';
import { BatchService } from '../../Services/batch.service';
import { MpsLabelPrinterService } from '../../Services/mps-label-printer.service';
import { MpsBatchForLabels, completeBagIds } from '../../Services/mps-label-builder';

/**
 * Finish Verifying, from the verify screen or the issues page. For an MPS batch it then prints
 * a bag label for every fully verified bag (a bag with a missing or unverified line gets none;
 * the pharmacist sorts those in MPS).
 */
@Injectable({ providedIn: 'root' })
export class VerifyFinishService {
  constructor(
    private api: BatchPickService,
    private batches: BatchService,
    private bagLabels: MpsLabelPrinterService,
  ) {}

  /**
   * Rejects when the batch couldn't be finished. Otherwise resolves with what went wrong with
   * the bag labels ("1 bag label didn't print"), or null when there was nothing wrong. Pass the
   * screen's state when it has one; otherwise it is fetched.
   */
  async finish(batchId: string, state?: BatchPickState): Promise<string | null> {
    await firstValueFrom(this.api.finishVerify(batchId));

    let detail: MpsBatchForLabels;
    let verified: Set<string>;
    try {
      // Verify statuses don't change at finish, so the screen's state is still right.
      const current = state ?? await firstValueFrom(this.api.state(batchId));
      if (current.source !== 'Mps') return null;
      verified = new Set(current.baskets.flatMap(b => b.medications)
        .filter(m => m.verifyScanStatus === 'Verified').map(m => m.medicationId));
      detail = (await firstValueFrom(this.batches.getBatchById(batchId)) as any)?.result;
      if (!detail) return "bag labels couldn't be loaded";
    } catch {
      return "bag labels couldn't be loaded";
    }

    // The batch is finished from here on: a problem with the labels is reported, never thrown.
    try {
      const ready = completeBagIds(detail, (_rxId, medicationId) => verified.has(medicationId));
      const missed = ready.length - (await this.bagLabels.printBags(detail, ready)).length;
      return missed === 0 ? null : `${missed} bag label${missed === 1 ? '' : 's'} didn't print`;
    } catch {
      return "bag labels couldn't be printed";
    }
  }
}
